import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Space, notification } from 'antd';
import { AppstoreOutlined } from '@ant-design/icons';
import api from '../services/api';
import AuthModal from '../components/AuthModal';
import EditModal from '../components/EditModal';

const UploadManagement = ({ card }) => {
  const [clubs, setClubs] = useState([]);
  const [_api, setApi] = notification.useNotification();
  const [selectedClub, setSelectedClub] = useState("");
  const [users, setUsers] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: "", tag: "", text: "", show: false, img: "", club: "",level:""
  });
  const [loading, setLoading] = useState(true);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // 批量操作相关状态
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  
  const setNotification = (config) => {
    config.description = config.message;
    config.message = config.type;
    _api.open(config);
  }
  
  // 加载社团列表
  useEffect(() => {
    const loadClubs = async () => {
      try {
        setLoading(true);
        const res = await api.getClubs();
        setClubs(res.data);
      } catch (e) {
        setNotification({
          "type": "error",
          "message": "社团加载失败:" + e.message,
        })
      } finally {
        setLoading(false);
      }
    };
    
    loadClubs();
  }, [card]);
  
  // 加载指定社团的用户
  const loadClubUsers = async (club) => {
    try {
      setLoading(true);
      const res = await api.getClubUsers(club);
      const usersWithStatus = await Promise.all(
        res.data.map(async (user) => {
          try {
            await api.findJson(club, user);
            return { name: user, uploaded: true };
          } catch {
            return { name: user, uploaded: false };
          }
        })
      );
      setUsers(usersWithStatus);
    } catch (e) {
      setNotification({
        "type": "error",
        "message": "用户加载失败:" + e.message,
      })
    } finally {
      setLoading(false);
    }
  };

  // 打开用户编辑模态框
  const handleEditUser = async (userName) => {
    try {
      setLoading(true);
      const res = await api.getUserInfo(selectedClub, userName);
      let user_data = res.data;
      user_data.img = `https://server.9ha.top/api/file/getImg?user=${userName}&club=${selectedClub}&card=${card}`;
      user_data.username = userName;
      setCurrentUser({"name": "", "tag": "", "text": "", "show": false, "img": "", "club": "",level:""});
      setCurrentUser(user_data);
      setEditModalVisible(true);
    } catch (e) {
      setNotification({
        "type": "error",
        "message": "用户加载失败:" + e.message,
      })
    } finally {
      setLoading(false);
    }
  };

  // 删除用户确认
  const showDeleteConfirm = (userName) => {
    setCurrentUser({...currentUser, name: userName});
    setDeleteVisible(true);
  };

  // 执行删除操作
  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      await api.deleteUser(selectedClub, currentUser.name);
      setNotification({
        "type": "success",
        "message": "删除成功"
      })
      setDeleteVisible(false);
      loadClubUsers(selectedClub);
    } catch (e) {
      setNotification({
        "type": "error",
        "message": "删除失败:" + e.message
      })
    } finally {
      setLoading(false);
    }
  };

  // 保存用户信息
  const handleSaveUser = async (values) => {
    try {
      setLoading(true);
      if (values.img.includes('data:image/')) {
        const img_data = values.img;
        await api.uploadImg(selectedClub, currentUser.username, img_data);
      }
      await api.setUserInfo(
        selectedClub, 
        currentUser.username, 
        values
      );
      setNotification({
        "type": "success",
        "message": "保存成功"
      })
      setEditModalVisible(false);
      loadClubUsers(selectedClub);
    } catch (e) {
      setNotification({
        "type": "error",
        "message": "保存失败:" + e.message
      })
    } finally {
      setLoading(false);
    }
  };

  // 自动刷新数据
  useEffect(() => {
    if (selectedClub) {
      const interval = setInterval(() => {
        setRefreshKey(prev => prev + 1);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedClub]);

  useEffect(() => {
    if (selectedClub) {
      loadClubUsers(selectedClub);
    }
  }, [selectedClub, refreshKey]);

  // 批量选择处理函数
  const handleBatchSelect = () => {
    if (isSelecting) {
      // 取消选择模式
      setSelectedUsers([]);
      setIsSelecting(false);
    } else {
      // 进入选择模式
      setIsSelecting(true);
    }
  };

  // 批量上传处理函数
  const handleConfirmUpload = async () => {
    try {
      setLoading(true);
      let success_count = 0;
      let failed_count = 0;
      
      // 准备所有上传请求
      const uploadPromises = selectedUsers.map(async (userName) => {
        try {
          // 获取用户信息
          const userInfo = await api.getUserInfo(selectedClub, userName);
          const userData = userInfo.data;
          
          // 获取图片
          const imgResponse = await api.get(
            `/api/file/getImg?user=${userName}&club=${selectedClub}&card=${card}`,
            { responseType: 'blob' }
          );

          // 使用 FileReader 转换为 base64
          const b_img = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result);
            };
            reader.readAsDataURL(imgResponse.data);
          });
          
          // 准备上传数据
          const uploadData = {
            ...userData,
            img: b_img,
            club: selectedClub
          };
          
          // 上传数据
          await api.setJson(uploadData);
          success_count++;
        } catch (error) {
          console.error(`上传失败 ${userName}:`, error);
          failed_count++;
        }
      });
      
      // 执行所有上传
      await Promise.all(uploadPromises);
      
      // 显示结果通知
      setNotification({
        type: "info",
        message: `上传完成`,
        description: `成功: ${success_count} 个，失败: ${failed_count} 个`
      });
      
      // 重置选择状态
      setSelectedUsers([]);
      setIsSelecting(false);
    } catch (error) {
      setNotification({
        type: "error",
        message: "批量上传失败",
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // 批量删除处理函数
  const handleBatchDelete = async () => {
    try {
      setLoading(true);
      let success_count = 0;
      let failed_count = 0;
      
      // 准备所有删除请求
      const deletePromises = selectedUsers.map(async (userName) => {
        try {
          await api.deleteUser(selectedClub, userName);
          success_count++;
        } catch (error) {
          console.error(`删除失败 ${userName}:`, error);
          failed_count++;
        }
      });
      
      // 执行所有删除
      await Promise.all(deletePromises);
      
      // 显示结果通知
      setNotification({
        type: "info",
        message: `删除完成`,
        description: `成功: ${success_count} 个，失败: ${failed_count} 个`
      });
      
      // 重置选择状态并刷新数据
      setSelectedUsers([]);
      setIsSelecting(false);
      loadClubUsers(selectedClub);
      setDeleteConfirmVisible(false);
    } catch (error) {
      setNotification({
        type: "error",
        message: "批量删除失败",
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => (
        <span style={{ color: record.uploaded ? 'green' : 'orange' }}>
          {record.uploaded ? '已上传' : '未上传'}
        </span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            onClick={() => handleEditUser(record.name)}
          >
            编辑
          </Button>
          <Button 
            type="link" 
            danger 
            onClick={() => showDeleteConfirm(record.name)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const clubColumns = [
    {
      title: '社团ID',
      dataIndex: 'club',
      key: 'club',
    },
    {
      title: '社团名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button 
          icon={<AppstoreOutlined />}
          disabled={!card || record.show===false}
          onClick={() => {
            setSelectedClub(record.club);
            loadClubUsers(record.club);
          }}
        >
          查看用户
        </Button>
      ),
    },
  ];

  // 行选择配置
  const rowSelection = isSelecting ? {
    selectedRowKeys: selectedUsers,
    onChange: (selectedRowKeys) => {
      setSelectedUsers(selectedRowKeys);
    },
  } : null;

  return (
    <div>
      {setApi}
      <Table
        dataSource={clubs}
        loading={loading}
        rowKey="club"
        columns={clubColumns}
        pagination={false}
      />

      {/* 用户管理对话框 */}
      <Modal
        title={`${selectedClub} - 用户管理`}
        visible={!!selectedClub}
        width={800}
        onCancel={() => setSelectedClub(null)}
        footer={null}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            onClick={handleBatchSelect}
            style={{ marginRight: 8 }}
          >
            {isSelecting ? "取消选择" : "选择操作"}
          </Button>
          
          {isSelecting && (
            <>
              <Button
                type="primary"
                onClick={handleConfirmUpload}
                disabled={selectedUsers.length === 0}
                style={{ marginRight: 8 }}
              >
                批量上传
              </Button>
              <Button
                type="primary"
                danger
                onClick={() => setDeleteConfirmVisible(true)}
                disabled={selectedUsers.length === 0}
              >
                批量删除
              </Button>
            </>
          )}
        </div>
        
        <Table
          dataSource={users}
          columns={userColumns}
          loading={loading}
          rowKey="name"
          pagination={false}
          bordered
          rowSelection={rowSelection}
        />
      </Modal>

      {/* 单个删除确认对话框 */}
      <Modal 
        title="删除用户"
        visible={deleteVisible}
        onOk={handleDeleteUser}
        onCancel={() => setDeleteVisible(false)}
        okText="删除"
        cancelText="取消"
        okType="danger"
      >
        确认删除用户 {currentUser.name} 吗？
      </Modal>

      {/* 批量删除确认对话框 */}
      <Modal 
        title="批量删除确认"
        visible={deleteConfirmVisible}
        onOk={handleBatchDelete}
        onCancel={() => setDeleteConfirmVisible(false)}
        okText="确认删除"
        cancelText="取消"
        okType="danger"
      >
        确认要删除选中的 {selectedUsers.length} 个用户吗？
      </Modal>

      {/* 编辑用户对话框 */}
      <EditModal
        visible={editModalVisible}
        initialValues={currentUser}
        onOk={handleSaveUser}
        onCancel={() => setEditModalVisible(false)}
      />
    </div>
  );
};

export default UploadManagement;